import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import Button from "../Button";

export type MultiSelectModalProps = {
    title: string;
    message: string;
    onAccept: () => void;
    onReject: () => void;
    /** When true, raises z-index so this modal appears above another open modal (e.g. nested confirm). */
    stackOnTop?: boolean;
    /** Optional content below the message (e.g. reject reason textarea). */
    children?: ReactNode;
    /** Backdrop click and Escape invoke this handler when provided. */
    onDismiss?: () => void;
};

export default function MultiSelectModal({
    title,
    message,
    onAccept,
    onReject,
    stackOnTop = false,
    children,
    onDismiss,
}: MultiSelectModalProps) {
    const handleDismiss = onDismiss ?? onReject;
    const modalRef = useRef<HTMLDivElement>(null);
    const acceptButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const modalEl = modalRef.current;
        if (!modalEl) return;

        // Skip scroll for nested confirms — parent modal already anchors the viewport (TS-REG-002).
        if (!stackOnTop) {
            modalEl.scrollIntoView({ block: 'center' });
        }

        const frameId = requestAnimationFrame(() => {
            acceptButtonRef.current?.focus();
        });
        return () => cancelAnimationFrame(frameId);
    }, [stackOnTop]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleDismiss();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [handleDismiss]);

    // Bootstrap 5: .modal 1055, .modal-backdrop 1050 — portaled to body so dialogs sit above page content.
    const modalStyle = stackOnTop
        ? { display: 'block' as const, zIndex: 1065 }
        : { display: 'block' as const, zIndex: 1055 };
    const backdropStyle = stackOnTop ? { zIndex: 1060 } : { zIndex: 1050 };

    return createPortal(
        <>
            <div
                ref={modalRef}
                className="modal fade show"
                style={modalStyle}
                tabIndex={-1}
                aria-modal="true"
                role="dialog"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content text-center p-4">
                        <div className="modal-body py-4">
                            <div className="text-success mb-3">
                                <i className="fas fa-check-circle" style={{ fontSize: '4rem' }} />
                            </div>
                            <h5 className="mb-2">{title}</h5>
                            <p className="text-muted mb-0">
                                {message}
                            </p>
                            {children != null ? <div className="mt-3 text-start w-100">{children}</div> : null}
                        </div>
                        <div className="modal-footer border-0 justify-content-center pb-4">
                            <Button
                                ref={acceptButtonRef}
                                type="button"
                                className="btn btn-primary"
                                onClick={onAccept}
                            >
                                Tamam
                            </Button>
                            <Button
                                type="button"
                                className="btn btn-outline-primary ms-2"
                                onClick={onReject}
                            >
                                İptal
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div
                className="modal-backdrop fade show"
                style={backdropStyle}
                onClick={handleDismiss}
                role="presentation"
            />
        </>,
        document.body,
    );
}

export type MultiSelectModalWithChildrenProps = {
    onAccept: () => void;
    onReject: () => void;
    acceptText?: string;
    rejectText?: string;
    dialogClassName?: string;
    children: React.ReactNode;
    /** Backdrop click and Escape invoke this handler when provided. */
    onDismiss?: () => void;
    acceptDisabled?: boolean;
    rejectDisabled?: boolean;
};

export function MultiSelectModalWithChildren({
    onAccept,
    onReject,
    acceptText = 'Tamam',
    rejectText = 'İptal',
    dialogClassName,
    children,
    onDismiss,
    acceptDisabled = false,
    rejectDisabled = false,
}: MultiSelectModalWithChildrenProps) {
    useEffect(() => {
        if (!onDismiss) return;
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onDismiss();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onDismiss]);

    return (
        <>
            <div
                className="modal fade show"
                style={{ display: 'block' }}
                tabIndex={-1}
                aria-modal="true"
                role="dialog"
            >
                <div className={`modal-dialog modal-dialog-centered ${dialogClassName ?? ''}`}>
                    <div className="modal-content text-center p-4">
                        <div className="modal-body py-4">
                            {children}
                        </div>
                        <div className="modal-footer border-0 justify-content-center pb-4">
                            <Button
                                type="button"
                                className="btn btn-primary"
                                onClick={onAccept}
                                disabled={acceptDisabled}
                            >
                                {acceptText}
                            </Button>
                            <Button
                                type="button"
                                className="btn btn-outline-primary ms-2"
                                onClick={onReject}
                                disabled={rejectDisabled}
                            >
                                {rejectText}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {onDismiss ? (
                <div
                    className="modal-backdrop fade show"
                    onClick={onDismiss}
                    role="presentation"
                />
            ) : (
                <div className="modal-backdrop fade show" />
            )}
        </>
    )
}
